/**
 * Presentation-only formatting. Dates render in the admin's own locale rather
 * than the ISO string the API sends — an ops tool is read at a glance, and
 * `2026-07-27T14:22:00Z` is not.
 *
 * ⚠️ Nothing here may be used on child data. A formatted date of birth is still
 * a date of birth (CLAUDE.md §4.1); see `components/redacted-value.tsx`.
 */

const locale = (): string =>
  typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-GB';

const DATE_TIME = new Intl.DateTimeFormat(locale(), {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const DATE_ONLY = new Intl.DateTimeFormat(locale(), { dateStyle: 'medium' });

const NUMBER = new Intl.NumberFormat(locale());

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? '—' : DATE_TIME.format(parsed);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  // A `YYYY-MM-DD` date has no time zone. Parsing it as UTC and formatting it
  // in local time can move it a day; anchoring at midday makes that impossible
  // for every real offset.
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return Number.isNaN(parsed.getTime()) ? '—' : DATE_ONLY.format(parsed);
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return NUMBER.format(value);
}

/** "3 days ago" — for audit trails, where the gap matters more than the stamp. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return 'Never';

  const seconds = Math.round((parsed.getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' });
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];

  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(seconds, 'second');
}

/** `YYYY-MM-DD` for the analytics range pickers, in UTC to match the rollup. */
export function toDateInput(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function daysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return toDateInput(d);
}
